import { Directive, ElementRef, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import Inputmask from 'inputmask';
import { Subscription } from 'rxjs';
import { AppService } from '../../../app/app.service';

@Directive({
    selector: '[app-restrict-input]',
    // eslint-disable-next-line @angular-eslint/no-host-metadata-property
    host: { '(paste)': '_handlePaste(null)' },
    standalone: true,
})
export class RestrictInputDirective implements OnInit, OnDestroy {
    private readonly el = inject(ElementRef);
    private readonly appService = inject(AppService);

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('app-restrict-input') inputType: string;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('app-restrict-input-control') control: AbstractControl;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('app-restrict-input-precision') precision: number | null;

    //Карта некоторых строк регулярных выражений, которые используются
    private regexMap = {
        integer: '^[-]{0,1}\\d+$',
        color: '^#+([a-fA-F0-9]{6})$',
        date: '[0-3][0-9]\\.[0-1][0-9]\\.[0-9]{4}',
        float: '^[+-]?([0-9]*[.])?[0-9]+$',
        double: '^-?\\d+(\\.\\d+)?$',
        words: '([A-z]*\\s)*',
        point25: '^-?[0-9]*(?:\\.25|\\.50|\\.75|)$',
        phoneEmail:
            // eslint-disable-next-line max-len
            '(\\+[0-9]\\([0-9][0-9][0-9]\\)[0-9][0-9][0-9]\\-[0-9][0-9]\\-[0-9][0-9])|([0-9]\\([0-9][0-9][0-9]\\)[0-9][0-9][0-9]\\-[0-9][0-9]\\-[0-9][0-9])|([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4})',
        email: '([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4})',
        // eslint-disable-next-line max-len
        phone: '(\\+[0-9]\\([0-9][0-9][0-9]\\)[0-9][0-9][0-9]\\-[0-9][0-9]\\-[0-9][0-9])|([0-9]\\([0-9][0-9][0-9]\\)[0-9][0-9][0-9]\\-[0-9][0-9]\\-[0-9][0-9])',
    };

    /**
     * Карта дефолтных масок
     * @private
     */
    private _mapMask = {};
    /**
     * Тип маски
     */
    private type = null;
    /**
     * Исключения для события вставки в поле данных
     */
    private excludeTypes = {
        date: true,
        dateTime: true,
    };

    public valueChanges$: Subscription;

    /**
     * Проставление значения в поле после вставки
     * @param $event событие
     * @private
     */
    @HostListener('paste', ['$event'])
    _handlePaste(_event: ClipboardEvent) {
        if (this.control && !this.excludeTypes[this.type]) {
            setTimeout(() => {
                //Получаем текущее значение маски
                if (this.el?.nativeElement?.inputmask) {
                    let currentMaskValue = null;
                    if (this.el?.nativeElement?.inputmask._valueGet()) {
                        currentMaskValue = this.el?.nativeElement?.inputmask?._valueGet();
                    }
                    //При отличии результатов ставим значение маски
                    if (currentMaskValue && currentMaskValue !== this.control.value) {
                        this.control.setValue(currentMaskValue);
                    }
                }
            });
        }
    }

    /**
     * Событие окончания инициализации компонента
     */
    ngOnInit() {
        this.defineInputType(this.inputType);
        if (this.control) {
            // eslint-disable-next-line rxjs-angular/prefer-takeuntil
            this.valueChanges$ = this.control.valueChanges.subscribe((value) => {
                if (value && this.el.nativeElement && this.el.nativeElement.inputmask) {
                    if (!this.el.nativeElement.inputmask.isValid()) {
                        this.control.setErrors({
                            inputMaskError: this.appService.language_dictionary.system_input_mask_error,
                        });
                        this.control.markAsTouched({ onlySelf: true });
                    }
                }
            });
        }
    }

    ngOnDestroy() {
        if (this.valueChanges$) {
            this.valueChanges$.unsubscribe();
        }
    }

    /**
     * Определение типа маски
     * @param type тип
     */
    public defineInputType(type: string) {
        if (typeof type !== 'string' || type.length < 1) {
            return;
        }

        this.type = type;
        Inputmask.remove(this.el.nativeElement);
        if (type === 'date') {
            const dateSeparator = this.appService.language_dictionary.system_date_format_separator;
            Inputmask({
                alias: 'datetime',
                inputFormat: this.appService.language_dictionary.system_date_format_input_mask,
                placeholder: '_',
                prefillYear: false,
                preValidation: (_buffer, pos, char, _isSelection, _opts, _maskset, _caretPos, _strict) => {
                    if ((pos === 2 || pos === 5) && char !== dateSeparator) {
                        return {
                            insert: [
                                { pos, c: dateSeparator },
                                { pos: pos + 1, c: char },
                            ],
                            caret: pos + 1,
                        };
                    }
                    return true;
                },
            }).mask(this.el.nativeElement);
        } else if (type === 'dateTime') {
            Inputmask({
                alias: 'datetime',
                inputFormat: 'dd.mm.yyyy HH:MM',
                placeholder: '_',
            }).mask(this.el.nativeElement);
        } else if (type === 'dayMonth') {
            Inputmask({
                alias: 'datetime',
                inputFormat: 'dd.mm',
                placeholder: '_',
                autoUnmask: true,
            }).mask(this.el.nativeElement);
        } else if (type === 'dateMonth') {
            Inputmask({
                alias: 'datetime',
                inputFormat: 'mm.yyyy',
                placeholder: '_',
            }).mask(this.el.nativeElement);
        } else if (type === 'dateYear') {
            Inputmask({
                alias: 'datetime',
                inputFormat: 'yyyy',
                placeholder: '_',
            }).mask(this.el.nativeElement);
        } else if (type === 'email') {
            Inputmask({ alias: 'email' }).mask(this.el.nativeElement);
        } else if (type === 'float') {
            const floatMask = this.precision
                ? '[-]9{1,}[.|,]{0,1}9{0,' + this.precision + '}'
                : '[-]9{1,}[.|,]{0,1}9{0,}';
            Inputmask({
                mask: floatMask,
                placeholder: '',
                removeMaskOnSubmit: true,
                substituteRadixPoint: true,
                onBeforeMask: (val, _opts) => {
                    if (Number.isInteger(val)) {
                        this.control.setValue(val + '.0');
                        return val + '.0';
                    }
                    return val;
                },
                onBeforePaste: (val, _opts) => {
                    val = String(val).replaceAll(' ', '');
                    const nval = parseFloat(val.replace(',', '.'));
                    if (Number.isInteger(nval)) {
                        return nval + '.0';
                    }
                    return val;
                },
                onincomplete: () => {
                    let value = this.control.value;
                    const splitval = value.replace(',', '.').split('.');
                    if ((splitval.length > 0 && splitval.length === 1) || splitval[1] === '') {
                        value = value.replace('.', '');
                        value = String(value) + '.0';
                    }
                    this.control.setValue(value, { eventEmit: false });
                    this.control.updateValueAndValidity({ emitEvent: false });
                },
            }).mask(this.el.nativeElement);
        } else if (type === 'integer') {
            Inputmask({
                mask: '[-]9{1,}',
                rightAlign: false,
                placeholder: '',
                onBeforePaste: (val, _opts) => {
                    val = String(val).replaceAll(' ', '').replace(',', '.');
                    const nval = parseFloat(val);
                    return isNaN(nval) ? val : nval.toFixed();
                },
            }).mask(this.el.nativeElement);
        } else if (type === 'page-number') {
            Inputmask({
                regex: '^[1-9][0-9]+$',
                placeholder: '',
            }).mask(this.el.nativeElement);
        } else if (type === 'color') {
            Inputmask({
                regex: '^#+([a-fA-F0-9]{6})$',
                placeholder: '',
            }).mask(this.el.nativeElement);
        } else if (this._mapMask[type]) {
            Inputmask({ mask: this._mapMask[type] }).mask(this.el.nativeElement);
        } else if (type === 'double') {
            Inputmask({
                mask: '[-]9{1,}[.|,|]{0,1}9{1,}',
                placeholder: '',
            }).mask(this.el.nativeElement);
            setTimeout(() => {
                this.control.addValidators([Validators.pattern(new RegExp(this.regexMap[type]))]);
                this.control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }, 1);
        } else if (this.regexMap[this.type]) {
            Inputmask({ regex: this.regexMap[this.type] }).mask(this.el.nativeElement);
        } else if (this.type && typeof this.type == 'string') {
            /*
                У Inputmask нет способа проверить валидность выражения (или я не нашёл).
                Невалидное выражение вешает браузер в бесконечном цикле.
                Ниже я добавил проверку на валидность, которая проверяет,
                соответствует ли число открытых фигурных скобок числу закрытых.
                Скорее всего, эта проверка неполная, и может быть улучшена.
            */
            const isValid = (this.type.match(/\{/g) || []).length === (this.type.match(/\}/g) || []).length;
            if (isValid) {
                Inputmask({
                    mask: this.type,
                    placeholder: '',
                }).mask(this.el.nativeElement);
            }
        }
    }

    /**
     * Определение маски по регулярному выражению
     * @param type тип
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('app-restrict-input-regex')
    public set defineInputRegexType(data: { regExp: string; fieldName: string }) {
        if (data.regExp?.length > 0) {
            setTimeout(() => {
                try {
                    const regex = new RegExp(data.regExp, 'i');
                    this.control.addValidators(Validators.pattern(regex));
                    this.control.updateValueAndValidity();
                } catch {
                    console.error(
                        this.appService.language_dictionary.custom_regex_error.replace('{0}', data.fieldName),
                    );
                }
            });
        }
    }
}
